import { GenericAbortSignal } from 'axios';

import { Paginated } from 'models/Paginated';
import http from 'services/http';

// Reports a coordinator sent from the browser extension ("Something not right?") when a portal
// page and its recorded script disagreed. The snapshot is the extension's (olio-extension
// src/lib/problem-report.ts, ReportSnapshot): shapes, counts and statuses only, never values.
// Every part of it can be missing — a report from a page no portal recognized has no fields,
// no script and no card state.

export type ProblemReportCategory = 'wrong_value' | 'missing_value' | 'missing_document' | 'something_else';

export const PROBLEM_REPORT_CATEGORY_LABELS: Record<ProblemReportCategory, string> = {
  wrong_value: 'A value is wrong',
  missing_value: 'A value is missing',
  missing_document: 'A document is missing',
  something_else: 'Something else',
};

export interface AdminPortalProblemReport {
  id: string;
  category: ProblemReportCategory;
  fields: string[];
  sourceSystem: string | null;
  pageFingerprint: string | null;
  newLayout?: boolean;
  extensionVersion: string | null;
  workflowId: string | null;
  portalScriptId: string | null;
  rumSessionId: string | null;
  resolvedAt: string | null;
  resolvedBy: { id: string; name: string } | null;
  resolutionLink: string | null;
  phiPurgedAt: string | null;
  createdAt: string;
  client: { id: string; name: string };
  user: { id: string; name: string; email: string };
}

export interface ReportFieldOutcome {
  field: string;
  status: 'found' | 'not_found' | 'ambiguous';
  matches: number;
  rung: string;
  shape: string | null;
}

export interface ReportFieldState {
  field: string;
  state: 'auto' | 'typed' | 'typed_over_auto' | 'empty';
}

export interface ReportScanCandidate {
  label: string;
  reasons: string[];
  repeatVeto: boolean;
  offered: boolean;
}

export interface ReportCaptureAttempt {
  at: number;
  path: string;
  outcome: 'settled' | 'failed';
  failure: string | null;
  mime: string | null;
  contentMatched: boolean | null;
  sameOrigin: boolean | null;
}

export interface ReportSnapshot {
  page?: {
    facts?: { url?: string; fingerprint?: string; frames?: { sameOrigin: number; crossOrigin: number } };
    fields?: ReportFieldOutcome[];
    pick?: { tag: string; label: string | null; scopes: string[]; css: string | null; shape: string } | null;
  };
  history?: {
    captures?: ReportCaptureAttempt[];
    evaluations?: { at: number; fields: ReportFieldOutcome[]; states: ReportFieldState[] }[];
    scan?: { total: number; offered: number; candidates: ReportScanCandidate[] } | null;
  };
  card?: { state: string | null; recognized: boolean };
  script?: { id: string; body: string } | null;
  build?: string;
}

export interface AdminPortalProblemReportDetail extends AdminPortalProblemReport {
  note: string | null;
  snapshot: ReportSnapshot;
  skeletonUrl?: string;
  pageCopyUrl?: string;
}

const URL = 'admin/portal_problem_reports';

export const adminPortalProblemReportQueryKeys = {
  base: ['adminPortalProblemReports'] as const,
  index: (sourceSystem: string) => ['adminPortalProblemReports', 'index', sourceSystem] as const,
  show: (id: string) => ['adminPortalProblemReports', 'show', id] as const,
};

export async function indexAdminPortalProblemReports(
  params: Record<string, any>,
  signal?: GenericAbortSignal
): Promise<Paginated<AdminPortalProblemReport>> {
  return http.get(URL, { params, signal }).then((res) => res.data);
}

export async function showAdminPortalProblemReport(id: string, signal?: GenericAbortSignal) {
  return http.get<{ data: AdminPortalProblemReportDetail }>(`${URL}/${id}`, { signal }).then((res) => res.data.data);
}

export async function updateAdminPortalProblemReport(
  id: string,
  input: { resolved: true; resolutionLink?: string } | { resolved: false }
) {
  return http.patch<{ data: AdminPortalProblemReportDetail }>(`${URL}/${id}`, input).then((res) => res.data.data);
}

export async function fetchAdminPortalProblemReportBundle(id: string) {
  return http.get<unknown>(`${URL}/${id}/bundle`).then((res) => res.data);
}
